import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CalendarCheck, Plus, Filter } from 'lucide-react';
import { bookingAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: { color: '#D97706', bg: '#FFFBEB' },
  confirmed: { color: '#059669', bg: '#ECFDF5' },
  checked_in: { color: '#2563EB', bg: '#EFF6FF' },
  checked_out: { color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { color: '#DC2626', bg: '#FEF2F2' },
  no_show: { color: '#9CA3AF', bg: '#F9FAFB' },
  refunded: { color: '#8B5CF6', bg: '#F5F3FF' },
};

const BookingsPage = () => {
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const fetchBookings = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await bookingAPI.getAll(params);
      setBookings(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [statusFilter, startDate, endDate]);

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.bookingNumber?.toLowerCase().includes(q) ||
      b.guest?.firstName?.toLowerCase().includes(q) ||
      b.guest?.lastName?.toLowerCase().includes(q) ||
      b.guest?.email?.toLowerCase().includes(q) ||
      b.property?.title?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bookings</h1>
          <p className="page-subtitle">{pagination.total} total bookings</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search booking #, guest, property..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {Object.keys(STATUS_STYLES).map(s => (
            <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="date" className="form-input" style={{ width: 160 }} value={startDate} onChange={e => setStartDate(e.target.value)} placeholder="From" />
          <span style={{ color: '#9CA3AF' }}>—</span>
          <input type="date" className="form-input" style={{ width: 160 }} value={endDate} onChange={e => setEndDate(e.target.value)} placeholder="To" />
        </div>
        {(statusFilter || startDate || endDate) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setStatusFilter(''); setStartDate(''); setEndDate(''); }}>
            Clear Filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Booking #</th>
                  <th>Guest</th>
                  <th>Property</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Nights</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="empty-state">
                        <CalendarCheck size={40} />
                        <h3>No bookings found</h3>
                        <p>Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : filtered.map(b => (
                  <tr key={b._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/bookings/${b._id}`)}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#F3F4F6', padding: '2px 8px', borderRadius: 4 }}>
                        {b.bookingNumber}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar avatar-sm">{b.guest?.firstName?.[0]}{b.guest?.lastName?.[0]}</div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{b.guest?.firstName} {b.guest?.lastName}</div>
                          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{b.guest?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{b.property?.title}</td>
                    <td>{b.checkIn ? format(new Date(b.checkIn), 'MMM d, yyyy') : '—'}</td>
                    <td>{b.checkOut ? format(new Date(b.checkOut), 'MMM d, yyyy') : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{b.nights}</td>
                    <td style={{ fontWeight: 600 }}>${b.pricing?.total?.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${b.payment?.status === 'paid' ? 'badge-green' : b.payment?.status === 'failed' ? 'badge-red' : 'badge-yellow'}`}>
                        {b.payment?.status || 'pending'}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{ color: STATUS_STYLES[b.status]?.color, background: STATUS_STYLES[b.status]?.bg }}>
                        {b.status?.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchBookings(p)}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
